import { ID, Response } from 'app/components/Table/core/types';

import { trimObject } from 'app/helpers';
import { getResponseError } from 'app/helpers/errors';

import { API_URL } from 'app/constants/environments';

import {
  Setting,
  SettingRequest,
  SettingType,
  SettingsQueryResponse,
  StockValues,
  WorkShift,
} from './type';
import axios, { AxiosResponse } from 'axios';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

export const CONFIGURATION_URL = `${API_URL}/api`;

const getSettings = (query: string): Promise<SettingsQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${CONFIGURATION_URL}/orgs/${organizationId}/settings?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<Setting>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};

const getSetting = (
  type: SettingType,
  params?: {
    name: string;
    location_id?: string;
  },
): Promise<Setting[]> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    return axios
      .get(`${CONFIGURATION_URL}/orgs/${organizationId}/settings/${type}`, {
        params,
      })
      .then(
        (
          d: AxiosResponse<{
            data: Setting[];
          }>,
        ) => {
          return d.data.data;
        },
      );
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

const getStockValue = (): Promise<StockValues[]> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .get(`${CONFIGURATION_URL}/orgs/${organizationId}/settings/${SettingType.stockLevel}`)
    .then(
      (
        d: AxiosResponse<{
          data: StockValues[];
        }>,
      ) => {
        return d.data.data;
      },
    );
};

const createSetting = (request: SettingRequest): Promise<Setting | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .post(`${CONFIGURATION_URL}/orgs/${organizationId}/settings`, trimObject(request))
    .then((response: AxiosResponse<Response<Setting>>) => response.data)
    .then((response: Response<Setting>) => response.data);
};

const updateSetting = (idSetting: ID, request: SettingRequest): Promise<Setting | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${CONFIGURATION_URL}/orgs/${organizationId}/settings/${idSetting}`, trimObject(request))
    .then((response: AxiosResponse<Response<Setting>>) => response.data)
    .then((response: Response<Setting>) => response.data);
};

const deleteSetting = async (idSetting: ID): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    await axios.delete(`${CONFIGURATION_URL}/orgs/${organizationId}/settings/${idSetting}`, {
      data: {
        type: SettingType.quantityUnit,
      },
    });
  } catch (error) {
    console.log(`Error deleting setting ID ${idSetting}`);
    throw error;
  }
};

const getWorkShift = (): Promise<WorkShift> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${CONFIGURATION_URL}/orgs/${organizationId}/settings/shift_work`).then(
    (
      d: AxiosResponse<{
        data: WorkShift;
      }>,
    ) => {
      return d.data.data;
    },
  );
};

export {
  getSettings,
  getSetting,
  createSetting,
  updateSetting,
  deleteSetting,
  getWorkShift,
  getStockValue,
};
